import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Restaurant as RestaurantIcon,
  CheckCircle as ReadyIcon,
  Schedule as PendingIcon,
  LocalFireDepartment as CookingIcon,
} from '@mui/icons-material';
import { PosService } from '../services/posService';
import type { OrdenPosResponse, OrdenPosDetalleResponse } from '../types/pos.types';
import { ESTADOS_ORDEN } from '../types/pos.types';

const ESTADO_ITEM_COLORS: Record<string, string> = {
  PENDIENTE: '#ff9800',
  EN_PREPARACION: '#f44336',
  LISTO: '#4caf50',
  ENTREGADO: '#9e9e9e',
  CANCELADO: '#9e9e9e',
};

const ESTADO_ITEM_ICONS: Record<string, React.ReactElement> = {
  PENDIENTE: <PendingIcon />,
  EN_PREPARACION: <CookingIcon />,
  LISTO: <ReadyIcon />,
};

export const KitchenOrderView: React.FC = () => {
  const [ordenes, setOrdenes] = useState<OrdenPosResponse[]>([]);
  const [loading, setLoading] = useState(false);

  const loadOrdenes = useCallback(async () => {
    try {
      setLoading(true);
      const data = await PosService.getOrdenes();
      const enCocina = data.filter(
        (o) =>
          o.tipoOrden === 'RESTAURANTE' &&
          (o.estado === ESTADOS_ORDEN.ABIERTA || o.estado === ESTADOS_ORDEN.EN_PREPARACION)
      );
      setOrdenes(enCocina);
    } catch (error) {
      console.error('Error loading kitchen orders:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrdenes();
  }, [loadOrdenes]);

  // Auto-refresh every 15 seconds
  useEffect(() => {
    const interval = setInterval(loadOrdenes, 15000);
    return () => clearInterval(interval);
  }, [loadOrdenes]);

  const getTimeSince = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Ahora';
    if (mins < 60) return `${mins} min`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  };

  const getItemsByStatus = (detalles: OrdenPosDetalleResponse[]) => {
    const pendientes = detalles.filter((d) => d.estado === 'PENDIENTE' || d.estado === 'EN_PREPARACION');
    const listos = detalles.filter((d) => d.estado === 'LISTO');
    return { pendientes, listos };
  };

  if (loading && ordenes.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <RestaurantIcon color="primary" />
        <Typography variant="h5" fontWeight="bold">
          Pantalla Cocina
        </Typography>
        <Chip
          label={`${ordenes.length} ordenes`}
          color="primary"
          size="small"
          sx={{ ml: 1 }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" size="small" onClick={loadOrdenes}>
          Actualizar
        </Button>
      </Box>

      {ordenes.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <RestaurantIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
          <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
            No hay ordenes en cocina
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {ordenes.map((orden) => {
            const { pendientes, listos } = getItemsByStatus(orden.detalles);
            const allReady = pendientes.length === 0 && listos.length > 0;

            return (
              <Grid key={orden.idOrdenPos} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <Card
                  variant="outlined"
                  sx={{
                    borderColor: allReady ? '#4caf50' : '#ff9800',
                    borderWidth: 2,
                    bgcolor: allReady ? '#4caf5010' : undefined,
                  }}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {orden.numeroOrden}
                        </Typography>
                        {orden.mesaNumero && (
                          <Typography variant="caption" color="text.secondary">
                            Mesa {orden.mesaNumero}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        label={getTimeSince(orden.fechaCreacion)}
                        size="small"
                        color={allReady ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    </Box>

                    {/* Items */}
                    <List dense disablePadding>
                      {orden.detalles
                        .filter((d) => d.estado !== 'CANCELADO' && d.estado !== 'ENTREGADO')
                        .map((item) => (
                          <ListItem key={item.idOrdenPosDetalle} disablePadding sx={{ py: 0.25 }}>
                            <Chip
                              icon={ESTADO_ITEM_ICONS[item.estado]}
                              size="small"
                              sx={{
                                bgcolor: `${ESTADO_ITEM_COLORS[item.estado]}20`,
                                color: ESTADO_ITEM_COLORS[item.estado],
                                height: 20,
                                mr: 0.5,
                                '& .MuiChip-icon': { fontSize: 14 },
                              }}
                              label=""
                            />
                            <ListItemText
                              primary={
                                <Typography variant="body2">
                                  <strong>{item.cantidad}x</strong> {item.descripcion}
                                </Typography>
                              }
                              secondary={item.notas}
                            />
                          </ListItem>
                        ))}
                    </List>

                    {orden.mozoNombre && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Mozo: {orden.mozoNombre}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default KitchenOrderView;
